import React from 'react';
import PropTypes from 'prop-types';
import requireScript from '../../../utils/requireScript';
import { customers, users } from "../../../ducks";
import { duckify, singleDuck } from "../../../utils/duckify";
import Placeholder from "../../Placeholder";
import FA from '../../../utils/FA';
import NachaMessage from '../NachaMessage';
import rollbar from 'utils/rollbarLogger';
import { paymentTypes, paymentMethodDisplayName, paymentInputDefaultProps, paymentInputPropTypes, submitButtonText } from "components/payments/paymentInputs/PaymentSelector";
import ConvenienceFeeCheckbox from "components/payments/paymentInputs/ConvenienceFeeCheckbox";
import PaymentProfileOptions from "../detailInputs/PaymentProfileOptions";
import PaymentDetailsInput from "../detailInputs/PaymentDetailsInput";
import { paymentsEnabled } from 'components/payments/stripe/stripeUtils';
import { apiFetch } from 'utils';

// Plaid error codes that shouldn't be sent to rollbar as errors
const IGNORE_PLAID_ERROR_CODES = [
  'INVALID_CREDENTIALS' // we don't need to know that you forgot your password
];

class PlaidAchInput extends React.Component {
  static propTypes = {
    ...paymentInputPropTypes,
    order: PropTypes.shape({
      entity_id: PropTypes.string.isRequired,
      entity_class: PropTypes.string.isRequired
    }).isRequired,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Amount in dollars

    buttonText: PropTypes.func,
    submittingText: PropTypes.func
  };

  static defaultProps = {
    ...paymentInputDefaultProps,
    amount: 0,
    buttonText: submitButtonText,
    submittingText: (customer, method) => "Submitting Account Info..."
  };

  constructor() {
    super();

    this.state = {
      error: null,
      plaidLinkHandler: null,
      disabled: false,
      submitting: false,
      savePaymentProfile: true,
      autopay: true
    }
  }

  handleClick() {
    // If we're in test mode, just send back a bogus token instead of logging in.
    if (global.PAYMENTS_TEST_MODE) {
      const { customer } = this.props;

      return this.handleSubmit(
        customer.ach_payment_info.dummy_bank_token || 'btok_plaid_1',
        { account_id: '12345' }
      );
    }

    this.setState({
      disabled: true,
      submitting: false
    });
    this.state.plaidLinkHandler.open();
  }

  handleExit() {
    this.setState({
      disabled: false,
      submitting: false
    });
  }

  fetchLinkToken() {
    this.setState({
      submitting: true
    });

    return apiFetch.post({}, 'plaid/link_token', {})
      .then(response => {
        this.setState({ submitting: false });
        return response.link_token;
      })
      .catch(error => {
        this.handleError(error);
        this.setState({ submitting: false });
        throw error;
      });
  }

  handleSubmit(token, metadata) {
    const { beforeSubmit, onSubmit, afterSubmit, paymentDetails } = this.props;
    const { account_id } = metadata;
    const { savePaymentProfile, autopay } = this.state;

    this.setState({
      submitting: true
    });

    return beforeSubmit()
      .then(() => apiFetch.post({}, 'plaid/stripe_token', { token, account_id }))
      .then(result => result.stripe_token)
      .then(token => onSubmit({
        payment_method: 'electronic check',
        token,
        savePaymentProfile,
        autopay,
        paymentDetails
      }))
      .then(afterSubmit)
      .catch(this.handleError.bind(this))
      .then(() => this.setState({ submitting: false }));
  }

  handleError(error) {
    const message = error.message || 'There was an error processing your bank account. Please try again.';
    this.setState({
      error: message,
      submitting: false,
      disabled: false
    });
  }

  handleEvent(eventName, metadata) {
    const infoLogger = rollbar.info;
    const errorLogger = rollbar.error;

    const isIgnoredError = IGNORE_PLAID_ERROR_CODES.includes(metadata.error_code);
    const isError = eventName == 'ERROR';

    const log = (isError && !isIgnoredError) ? errorLogger : infoLogger;
    const eventDisplayName = isError ? `${eventName} - ${metadata.error_code}` : eventName;

    log(`Plaid event - ${eventDisplayName}`, metadata);
  }

  UNSAFE_componentWillMount() {
    const { customer } = this.props;

    if (!global.PAYMENTS_TEST_MODE) {
      requireScript('https://cdn.plaid.com/link/v2/stable/link-initialize.js', { unless: global.Plaid })
        .then(async () => {
          const token = await this.fetchLinkToken();
          this.setState({
            plaidLinkHandler: global.Plaid.create({
              token: token,
              onSuccess: (public_token, metadata) => this.handleSubmit(public_token, metadata),
              onExit: this.handleExit.bind(this),
              onEvent: this.handleEvent.bind(this)
            })
          });
        });
    }
  }

  convenienceFeeConfirmationRequired() {
    const {
      customer: {
        payment_config: {
          payment_methods: {
            [paymentTypes.ACH]: {
              convenience_fee: {
                show_confirmation_message = false
              } = {}
            } = {}
          } = {}
        } = {}
      },
      convenienceFee
    } = this.props;

    return show_confirmation_message && (convenienceFee != 0);
  }

  render() {
    const {
      billingConfig,
      customer,
      amount,
      paymentDetails,
      showSaveProfileOption,
      showAutopayOption
    } = this.props;

    const buttonText = this.props.buttonText(customer, 'electronic check');
    const submittingText = this.props.submittingText(customer, 'electronic check');

    const {
      error,
      submitting,
      plaidLinkHandler,
      savePaymentProfile,
      autopay,
      convenienceFeeAccepted
    } = this.state;

    const disabled = this.props.disabled || this.state.disabled;
    const autopayEnabled = customer.billing_enabled;

    const convenienceFeeOk = convenienceFeeAccepted || !this.convenienceFeeConfirmationRequired();

    // StripeCustomGateway records all transactions against a Stripe Customer and therefore requires
    // a saved payment method for ACH transactions.
    const requireSaveProfile = customer.ach_payment_gateway === 'StripeCustomGateway';

    if (!paymentsEnabled(customer.ach_payment_gateway, customer.ach_payment_info)) {
      return <div className={'alert alert-danger'}>
        <FA name={'warning'} nbsp />
        Electronic Check payments have not been configured.  Please contact the system administrator.
      </div>;
    }

    return <Placeholder.loading ready={global.PAYMENTS_TEST_MODE || !!plaidLinkHandler} content={() =>
      <div className={'payment-details'}>
        <div className={'alert alert-info'}>
          <p>
            {customer.long_name} provides a fast and secure way to process {paymentMethodDisplayName(customer, paymentTypes.ACH)} withdrawals by linking directly with your bank.
          </p>
          <ul>
            <li>No need to find your routing or account number</li>
            <li>Safe and secure, sign in directly to your account</li>
            <li>Your credentials will never be made accessible to {customer.long_name}</li>
          </ul>
        </div>

        {error && <div className={'alert alert-danger'}>{error}</div>}

        <fieldset className={'payment-ach-plaid-input'}>
          <PaymentDetailsInput onChange={this.props.paymentDetailsChanged} customer={customer} />
        </fieldset>

        {this.convenienceFeeConfirmationRequired() && (
          <ConvenienceFeeCheckbox
            customer={customer}
            checked={convenienceFeeAccepted}
            paymentMethod={paymentTypes.ACH}
            onChange={(...params) => {
              this.setState({ convenienceFeeAccepted: !this.state.convenienceFeeAccepted });
            }}
            disabled={submitting}
          />
        )}


        <PaymentProfileOptions
          billingConfig={billingConfig}
          customer={customer}
          showSaveProfileOption={showSaveProfileOption}
          showAutopayOption={showAutopayOption}
          savePaymentProfile={savePaymentProfile}
          requireSaveProfile={requireSaveProfile}
          paymentMethod={'electronic check'}
          autopay={autopay}
          onChange={(props) => this.setState(props)}
          disabled={submitting}
        />

        {submitting ?
          <button className={'btn btn-primary'} type={'button'} disabled>
            <FA name={'spinner'} spin nbsp />
            {submittingText}
          </button>
          :
          <button className={'btn btn-primary'}
            type={'button'}
            onClick={() => this.handleClick()}
            disabled={!convenienceFeeOk || !paymentDetails.valid || !!disabled}
          >
            Select Bank and {buttonText}
          </button>
        }

        <NachaMessage amount={amount}
          buttonText={buttonText}
          customer={customer} />
      </div>
    } />;
  }
}

function mapDucksToProps(ownProps) {
  return {
    customer: singleDuck(customers, 'current'),
    user: singleDuck(users, 'current')
  };
}

function mapStateToProps(state, ownProps) {
  return {};
}

function mapDispatchToProps(dispatch, ownProps) {
  return {};
}

export default duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)(PlaidAchInput);
export const unduckified = PlaidAchInput;
